// Ported from cartadigital-pos app/domain/pricing/delivery-zones.ts — keep in
// behavioral parity with backend delivery-zone selection (poly.ts).
export type IGeoCoords = [number, number];

export interface IDeliveryZoneInput {
  id: number;
  cost: number | null | undefined;
  pointsJson: string | null | undefined;
  /**
   * IMPORTANT (parity note):
   * - Current backend V2 delivery-zone selection does NOT filter by `enabled`.
   * - To stay parity-correct, this module ignores `enabled` by default.
   * - If/when backend is updated to respect `enabled`, callers can opt-in via
   *   `respectEnabled: true`.
   */
  enabled?: boolean | number | null | undefined;
}

export interface IResolveDeliveryCostResult {
  deliveryCost: number;
  matchedZoneId: number | null;
  /**
   * True when zoning is enabled and coords are valid, but no zone contains the coords.
   * Callers can use this to display a warning, or to block order creation if desired.
   */
  isOutsideZone: boolean;
}

function normalizeFiniteNumber(value: unknown): number | null {
  if (value == null) return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}

function isZoneEnabled(value: IDeliveryZoneInput['enabled']): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  return true;
}

export function parseDeliveryZonePointsJson(
  pointsJson: string | null | undefined
): number[] {
  if (!pointsJson) return [];
  try {
    const parsed: unknown = JSON.parse(pointsJson);
    if (!Array.isArray(parsed)) return [];
    const out: number[] = [];
    for (const v of parsed) {
      const n = normalizeFiniteNumber(v);
      if (n != null) out.push(n);
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * Ported from `cartadigital-api/src/utilities/poly.ts` with the same
 * return semantics (true/false/0).
 */
function pointInPolygon(
  p: IGeoCoords,
  polygon: Array<Array<IGeoCoords>>
): boolean | 0 {
  let i = 0;
  let ii = 0;
  let k = 0;
  let f = 0;
  let u1 = 0;
  let v1 = 0;
  let u2 = 0;
  let v2 = 0;
  let currentP: IGeoCoords | null = null;
  let nextP: IGeoCoords | null = null;

  const x = p[0];
  const y = p[1];

  const numContours = polygon.length;
  for (i; i < numContours; i++) {
    ii = 0;
    const contourLen = polygon[i].length - 1;
    const contour = polygon[i];

    currentP = contour[0] ?? null;
    if (!currentP) continue;
    u1 = currentP[0] - x;
    v1 = currentP[1] - y;

    for (ii; ii < contourLen; ii++) {
      nextP = contour[ii + 1] ?? null;
      if (!nextP) continue;

      v2 = nextP[1] - y;

      if ((v1 < 0 && v2 < 0) || (v1 > 0 && v2 > 0)) {
        currentP = nextP;
        v1 = v2;
        u1 = currentP[0] - x;
        continue;
      }

      u2 = nextP[0] - p[0];

      if (v2 > 0 && v1 <= 0) {
        f = u1 * v2 - u2 * v1;
        if (f > 0) k = k + 1;
        else if (f === 0) return 0;
      } else if (v1 > 0 && v2 <= 0) {
        f = u1 * v2 - u2 * v1;
        if (f < 0) k = k + 1;
        else if (f === 0) return 0;
      } else if (v2 === 0 && v1 < 0) {
        f = u1 * v2 - u2 * v1;
        if (f === 0) return 0;
      } else if (v1 === 0 && v2 < 0) {
        f = u1 * v2 - u2 * v1;
        if (f === 0) return 0;
      } else if (v1 === 0 && v2 === 0) {
        if (u2 <= 0 && u1 >= 0) {
          return 0;
        } else if (u1 <= 0 && u2 >= 0) {
          return 0;
        }
      }
      currentP = nextP;
      v1 = v2;
      u1 = u2;
    }
  }

  if (k % 2 === 0) return false;
  return true;
}

function polyIsClockwise(poly: Array<IGeoCoords>): boolean {
  let sum = 0;
  for (let i = 0; i < poly.length - 1; i++) {
    const cur = poly[i];
    const next = poly[i + 1];
    if (!cur || !next) continue;
    sum += (next[0] - cur[0]) * (next[1] + cur[1]);
  }
  return sum > 0;
}

function arrayToPairs(array: number[]): Array<IGeoCoords> {
  return array.reduce<Array<IGeoCoords>>((result, _value, index, arr) => {
    if (index % 2 === 0) {
      const a = arr[index];
      const b = arr[index + 1];
      if (typeof a === 'number' && typeof b === 'number') {
        result.push([a, b]);
      }
    }
    return result;
  }, []);
}

export function getContainingDeliveryZone<T extends { points: number[] }>(
  coords: IGeoCoords,
  deliveryZones: readonly T[]
): T | null {
  for (const zone of deliveryZones) {
    const poly = arrayToPairs(zone.points);

    // Need at least 3 points.
    if (poly.length < 3) continue;

    const first = poly[0];
    const last = poly[poly.length - 1];
    // Keep backend's exact closure condition (note: it's `&&` in the original).
    if (first && last && first[0] !== last[0] && first[1] !== last[1])
      poly.push(first);

    if (!polyIsClockwise(poly)) poly.reverse();
    const res = pointInPolygon(coords, [poly]);
    if (res === true || res === 0) {
      return zone;
    }
  }
  return null;
}

/**
 * Delivery-cost resolution with zoning.
 *
 * Mirrors backend's core behavior:
 * - If zoning is enabled and coords are valid, pick the first zone (by cost ASC)
 *   that contains the coords.
 * - If no zone matches, callers can decide what to do (we return base cost + flag).
 */
export function resolveDeliveryCost(params: {
  baseDeliveryCost: number;
  zonedDelivery: boolean;
  zonedFee: boolean;
  deliveryInfo?: { lat?: number; lng?: number } | null;
  deliveryZones: readonly IDeliveryZoneInput[];
  /**
   * If true, filters out zones where enabled=false/0.
   * Defaults to false for backend parity.
   */
  respectEnabled?: boolean;
  /**
   * When true, throws if coords are valid + zones exist but no zone contains them.
   * Default false to keep totals computation non-throwing.
   */
  strictOutsideZone?: boolean;
}): IResolveDeliveryCostResult {
  const base =
    typeof params.baseDeliveryCost === 'number' &&
    Number.isFinite(params.baseDeliveryCost)
      ? params.baseDeliveryCost
      : 0;

  if (!params.zonedDelivery || !params.zonedFee) {
    return { deliveryCost: base, matchedZoneId: null, isOutsideZone: false };
  }

  const lat = normalizeFiniteNumber(params.deliveryInfo?.lat);
  const lng = normalizeFiniteNumber(params.deliveryInfo?.lng);
  if (lat == null || lng == null) {
    return { deliveryCost: base, matchedZoneId: null, isOutsideZone: false };
  }

  // Parse + normalize zones.
  const zones = params.deliveryZones
    .filter((z) => (params.respectEnabled ? isZoneEnabled(z.enabled) : true))
    .map((z) => {
      const cost = normalizeFiniteNumber(z.cost);
      const points = parseDeliveryZonePointsJson(z.pointsJson);
      return {
        id: z.id,
        cost,
        points,
      };
    })
    .filter(
      (z): z is { id: number; cost: number; points: number[] } =>
        typeof z.cost === 'number' && z.points.length >= 6
    )
    .sort((a, b) => a.cost - b.cost);

  if (zones.length === 0) {
    return { deliveryCost: base, matchedZoneId: null, isOutsideZone: false };
  }

  const zone = getContainingDeliveryZone([lat, lng], zones);
  if (!zone) {
    if (params.strictOutsideZone) {
      throw new Error('El restaurante no entrega en esa dirección');
    }
    return { deliveryCost: base, matchedZoneId: null, isOutsideZone: true };
  }

  return {
    deliveryCost: zone.cost,
    matchedZoneId: zone.id,
    isOutsideZone: false,
  };
}
